import { useCallback } from 'react';

import { assertNever } from '@/helpers/utils';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { VehiclesUtil } from '@/modules/vehicles/vehiclesUtil';

export const useIsVehicleBeingUsedCheck = () => {
  const isVehicleBeingUsed = useCallback((id: string): boolean => {
    const areas = areasToFlatCollection(useFloorplanDesignStore.getState().design.areas);

    // eslint-disable-next-line array-callback-return
    const isInUse = areas.some((area) => {
      const { areaType } = area;

      switch (areaType) {
        case 'HIGHWAY':
          return area.highwayPoiSpecs.some((item) => item.vehicleTypeId === id);
        case 'RACKING':
          return (
            area.vehicleTypeId === id ||
            (VehiclesUtil.isShuttleVehicle(id) &&
              area.rackSpec.loadTypes.some((item) => item.loadTypeId === id))
          );
        case 'SINGULAR':
          return area.loadHandlingPoiSpecs.some((item) => item.vehicleTypeId === id);
        case 'STATIONARY':
          return area.stationaryPoiSpecs.some((item) => item.vehicleTypeId === id);
        case 'MANUAL':
          return area.vehicleTypeIds.includes(id);
        default:
          assertNever(areaType);
      }
    });

    return isInUse;
  }, []);

  const isVehicleBeingUsedInShuttleRack = useCallback((id: string): boolean => {
    const areas = areasToFlatCollection(useFloorplanDesignStore.getState().design.areas);

    return areas.some((area) => {
      if (area.areaType !== 'RACKING') {
        return false;
      }

      const isShuttleRack = area.rackSpec.loadTypes.some(({ loadTypeId }) =>
        VehiclesUtil.isShuttleVehicle(loadTypeId),
      );

      if (!isShuttleRack) {
        return false;
      }

      const isRackVehicle = area.vehicleTypeId === id;
      const isShuttleLoadType = area.rackSpec.loadTypes.some(({ loadTypeId }) => loadTypeId === id);

      return isRackVehicle || isShuttleLoadType;
    });
  }, []);

  return {
    isVehicleBeingUsed,
    isVehicleBeingUsedInShuttleRack,
  };
};
